import React, { Component, memo } from "react";
import moment from "moment";
import classNames from "classnames";
import { startCase, isEmpty } from "lodash";
import { Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import Form from "../../../../common/Form";
import { CustomMenuItem } from "../../../../common";
import { SearchIcon } from "../../../../common/SearchIcon";
import RemoveIcon from "../../../../common/RemoveIcon";
import InfoBlock from "../../../../common/InfoBlock";
import {
  FORM_FIELD_TYPE,
  TAG_TYPE,
  CAMPAIGN_ACTIONS,
  CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST,
} from "../../../../../constants";
import { getDateRange, getRestrictedDateLimit } from "../../utils/campaignCreate";
import styles from "../../../../../styles/CampaignCreate/CampaignForm.module.scss";
import {
  searchContentTypes,
  searchBrandsWithAdvertiserId,
  categorySearchWithBrandAndText,
  searchCpls,
} from "../../../../../api";
// import RightPanel from "../../../../common/RightPanel";

class BasicDetails extends Component {
  state = {
    duplicateName: false,
    minDate: undefined,
    maxDate: undefined,
    productCategories: [],
    canEditContent: false,
    showAddBrandPanel: false,
    newBrandName: "",
  };

  componentDidMount() {
    const { media, campaign } = this.props;
    const { fromDate, toDate } = getRestrictedDateLimit(
      media?.targetGroup?.validity,
      campaign.validity
    );
    this.setState({
      minDate: fromDate ? new Date(fromDate) : undefined,
      maxDate: toDate ? new Date(toDate) : undefined,
      canEditContent: !media?.contentId,
    });
  }

  getRestrictedDateLimit = () => {
    const { media, campaign } = this.props;
    const validity = isEmpty(media?.targetGroup?.validity)
      ? campaign.validity
      : media.targetGroup.validity;
    if (!isEmpty(validity)) {
      const { fromDate, toDate } = validity;
      this.setState({
        minDate: fromDate ? new Date(fromDate) : undefined,
        maxDate: toDate ? new Date(toDate) : undefined,
      });
    }
  };

  onNameChange(value) {
    const { mediaList, onInputChange } = this.props;
    const allMediaNames = mediaList.map((t) => t.name);
    onInputChange(value, "name", allMediaNames.includes(value));
  }

  validateMediaContent(field) {
    const { media } = this.props;

    // For contentId field validation:
    // - Valid if contentId exists (real content selected), OR
    // - Valid if contentName exists (expected content name provided)
    if (field === "contentId") {
      return Boolean(media.contentId) || Boolean(media.contentName);
    }

    // For other fields (contentTypeId, contentDurationInSeconds):
    // - Only required when no contentId is selected
    if (!media.contentId) {
      return Boolean(media[field]);
    }

    return Boolean(media[field]);
  }

  handleBrandCreated = (createdBrand) => {
    const { onInputChange } = this.props;

    if (!createdBrand?.id) {
      onInputChange(null, "brandId");
      onInputChange("", "brandName");
      return;
    }

    const normalizedBrand = {
      ...createdBrand,
      id: createdBrand.id,
      name: createdBrand.name || createdBrand.displayName || "",
      displayName: createdBrand.displayName || createdBrand.name || "",
      type: createdBrand.type || "parentBrand",
    };

    onInputChange(normalizedBrand, "brandId");
    onInputChange(normalizedBrand.name, "brandName");
  };

  isSelectedCategory = (data) => {
    const {
      media: { brand },
    } = this.props;
    return brand?.categories?.find(
      (category) =>
        category.categoryId === data.categoryId &&
        category.subCategory1Id === data.subCategory1Id &&
        category.subCategory2Id === data.subCategory2Id
    );
  };

  arrowWithName = (n1, n2, n3) => {
    return [n1, n2, n3].filter((n) => n !== undefined && n !== null && n.length > 0).join(" > ");
  };

  categoryMenuRenderer = (data) => {
    return this.arrowWithName(data.categoryName, data.subCategory1Name, data.subCategory2Name);
  };

  renderBrandMenuName = (item) => {
    switch (item.type) {
      case "parentBrand":
        return `${item.parentBrandName}`;
      case "individualBrand":
        return `${item.parentBrandName}  ->  ${item.individualBrandName}`;
      case "product":
        return `${item.parentBrandName}  ->  ${item.individualBrandName}  ->  ${item.productName}`;
      case "variant":
        return `${item.parentBrandName}  ->  ${item.individualBrandName}  ->  ${item.productName}  ->  ${item.variantName}`;
      default:
        return `${item.parentBrandName}`;
    }
  };
  renderCategoryMenuItem = (data, { modifiers, handleClick }) => {
    return (
      <CustomMenuItem
        key={`${data.categoryId} ${data.subCategory1Id} ${data.subCategory2Id}`}
        active={modifiers?.active}
        icon={this.isSelectedCategory(data) ? IconNames.TICK : IconNames.BLANK}
        onClick={handleClick}
        text={this.categoryMenuRenderer(data)}
        shouldDismissPopover={false}
      />
    );
  };

  onBrandQueryChange = async (text) => {
    const { media } = this.props;

    if (media?.brandId) {
      const categories = await categorySearchWithBrandAndText(media?.brandId, text);
      const categoriesResponse = categories.data;

      const transformedCategories = categoriesResponse.map((categoryInfo) => {
        const category = categoryInfo?.category || {};
        const subCategory1 = categoryInfo?.subCategory1 || {};
        const subCategory2 = categoryInfo?.subCategory2 || {};

        return {
          categoryId: category.id || "",
          categoryName: category.name || "",
          subCategory1Id: subCategory1.id || "",
          subCategory1Name: subCategory1.name || "",
          subCategory2Id: subCategory2.id || "",
          subCategory2Name: subCategory2.name || "",
          updatedAt: category.updatedAt || "",
        };
      });
      this.setState({
        productCategories: transformedCategories,
      });
    }
  };
  handleCloseBrandPanel = () => {
    this.setState({
      showAddBrandPanel: false,
      newBrandName: "",
    });
  };

  handleBrandCreateSuccess = (brand, isApproved) => {
    const { onInputChange } = this.props;

    if (isApproved && brand?.id) {
      onInputChange(brand, "brandId");
    } else {
      onInputChange(null, "brandId");
    }

    this.handleCloseBrandPanel();
  };

  render() {
    const {
      hasFormSaved,
      targetGroups,
      // media: { targetGroupId },
      media,
      onInputChange,
      isDuplicateName,
      removeCategory,
      campaign,
      // categoryList,
      // categorySearch,
    } = this.props;

    const { productCategories, canEditContent } = this.state;

    const showError = hasFormSaved;

    const allowModifyingStartDate = CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[
      CAMPAIGN_ACTIONS.EDIT_VALIDITY
    ].includes(media.status);

    const validity = campaign.id && !!campaign.validity ? campaign.validity : {};

    const availableCategories = productCategories.filter(
      (category) =>
        !media?.categories?.some(
          (selectedCategory) =>
            selectedCategory?.categoryId === category?.categoryId &&
            selectedCategory?.subCategory1Id === category?.subCategory1Id &&
            selectedCategory?.subCategory2Id === category?.subCategory2Id
        )
    );

    //!Date Range Validation
    const today = moment().startOf("day");
    const isPastCampaign = today > new Date(validity?.toDate);

    let dateRangeMin = undefined;
    let dateRangeMax = undefined;
    let endDateMin = undefined;
    let endDateMax = undefined;

    if (!isPastCampaign) {
      //date range: {from: tg start date, to: tg end date}
      dateRangeMin = validity?.fromDate ? new Date(validity?.fromDate) : new Date();
      dateRangeMax = validity?.toDate ? new Date(validity.toDate) : undefined;
      //end date: {from: media start date, to: tg end date}
      endDateMin = media.validity?.fromDate ? new Date(media.validity.fromDate) : undefined;
      endDateMax = dateRangeMax;
    }

    const isMediaEditDisabled = media.status === "Active" || media.status === "Paused";

    return (
      <InfoBlock header="Media Details" childClassName={styles.childWrapper}>
        <div
          className={classNames(
            "flex flex-wrap clearfix",
            styles.formContainer,
            styles.basicDetails
          )}
        >
          <Form
            isFormGroup={false}
            config={[
              {
                id: "name",
                label: "Media Name",
                type: FORM_FIELD_TYPE.INPUT,
                size: {
                  lg: 5,
                  md: 5,
                },
                placeholder: "Enter Name",
                value: media.name || "",
                onChange: (e) => this.onNameChange(e.target.value),
                error: (hasFormSaved && !media.name) || this.state.duplicateName,
                errorMessage: isDuplicateName ? "Duplicate Media Name" : "Please Enter Name",
              },
              {
                id: "targetGroup",
                key: "targetGroup",
                type: FORM_FIELD_TYPE.SELECT,
                size: {
                  lg: "col-lg-6 col-lg-offset-1",
                  md: "col-md-6 col-md-offset-1",
                },
                containerClassName: styles.mediaFormField,
                title: "Target Group",
                list: targetGroups.map((t) => ({
                  ...t,
                  value: (
                    <div>
                      {t.name}{" "}
                      <span
                        style={{ color: "gray", fontSize: "12px" }}
                      >{` (screens: ${t.noOfScreens})`}</span>
                    </div>
                  ),
                })),
                // selectedList: !isEmpty(media.targetGroup) ? [media.targetGroup.name] : [],
                selectedList: !isEmpty(media.targetGroupName) ? [media.targetGroupName] : [],
                singleSelect: true,
                compareKey: "id",
                textKey: "name",
                usePortal: false,
                placeHolder: "Select Target Group",
                showLabelInButton: false,
                onSelect: ([data]) =>
                  onInputChange(
                    targetGroups.find((t) => t.id === data.id),
                    "targetGroup"
                  ),
                disabled: isMediaEditDisabled,
                errorMessage: "Please Select Target Group",
                error: hasFormSaved && isEmpty(media.targetGroupName),
              },
              {
                id: "validity",
                type: FORM_FIELD_TYPE.DATE_RANGE_PICKER,
                formatDate: (date) => moment(date).format("DD/MM/YYYY"),
                title: "Validity",
                containerClassName: "col-11",
                size: {
                  lg: 6,
                  md: 6,
                },
                handleDateChange: (dateRange) => onInputChange(dateRange, "validity"),
                dateRange: getDateRange(media?.validity),
                // disabled: Object.keys(media.targetGroup).length === 0 || isPastCampaign, //!condition to disable might change in future
                disabled: isPastCampaign, //!condition to disable might change in future
                minDate: dateRangeMin,
                maxDate: dateRangeMax,
                errorMessage: "Please Enter Validity",
                error: hasFormSaved && (isEmpty(media.targetGroupName) || !media?.validity),
                isHidden: !allowModifyingStartDate,
                initialMonth: media.validity?.fromDate
                  ? new Date(media.validity.fromDate)
                  : new Date(dateRangeMin),
              },
              {
                id: "startDate",
                type: FORM_FIELD_TYPE.INPUT,
                label: "Validity (Start Date)",
                styles: { width: "50%" },
                size: { lg: 2, md: 2 },
                value: moment(media.validity.fromDate).format("MMM DD, YYYY"),
                disabled: !allowModifyingStartDate,
                isHidden: allowModifyingStartDate,
              },
              {
                id: "endDate",
                type: FORM_FIELD_TYPE.DATE,
                title: "Validity (End Date)",
                containerClassName: "col-11",
                size: { lg: 2, md: 2 },
                onChange: (v) => onInputChange([null, v], "validity"),
                value: new Date(media.validity.toDate),
                placeholder: "End Date",
                isHidden: allowModifyingStartDate,
                minDate: endDateMin,
                maxDate: endDateMax,
                disabled: isPastCampaign, //!condition to disable might change in future
              },
              {
                type: FORM_FIELD_TYPE.LABEL,
                title: "Total Duration",
                size: {
                  lg: !allowModifyingStartDate ? "col-lg-6 col-lg-offset-2" : 6,
                  md: !allowModifyingStartDate ? "col-md-6 col-md-offset-1" : 6,
                },
                value:
                  !isEmpty(media?.validity) && media.validity.fromDate && media.validity.toDate
                    ? ` ${
                        moment(media.validity.toDate).diff(
                          moment(media.validity.fromDate),
                          "days"
                        ) + 1
                      } days`
                    : "0 days",
              },
              // {
              //   id: "purchaseOrderNumber",
              //   label: "Purchase Order Number",
              //   type: FORM_FIELD_TYPE.INPUT,
              //   className: "col-11",
              //   size: {
              //     lg: "col-lg-6 col-lg-offset-2",
              //     md: "col-md-6 col-md-offset-2",
              //   },
              //   placeholder: "Enter Purchase Order Number",
              //   value: media.purchaseOrderNumber || "",
              //   onChange: (e) => onInputChange(e.target.value, "purchaseOrderNumber"),
              //   disabled: false,
              // },
              [
                {
                  id: "contentId",
                  label: "Content",
                  type: FORM_FIELD_TYPE.API_SEARCH,
                  size: {
                    lg: 5,
                    md: 5,
                  },
                  placeholder: "Select Content",
                  showSubText: false,
                  resetOnSelect: false,
                  tagTypes: TAG_TYPE.CONTENT,
                  query: media.contentDisplayName ?? media.contentName ?? "",
                  // Pass selectedItem to sync ApiSearch's internal state - clear it when no content is selected
                  selectedItem: media.contentId
                    ? {
                        id: media.contentId,
                        name: media.contentName,
                        displayName: media.contentName,
                      }
                    : null,
                  fetchAction: (value) => {
                    onInputChange(value, "contentDisplayName");
                    // Clear contentName when user types something different to prevent old selected value from persisting
                    if (media.contentName && value !== media.contentName) {
                      onInputChange("", "contentName");
                      onInputChange(null, "contentId");
                      onInputChange("", "contentTypeId");
                      onInputChange("", "contentDurationInSeconds");
                    }
                    return searchCpls(value);
                  },
                  parseResults: (response) => {
                    const { data } = response;
                    const modifiedData = data.map((item) => ({
                      id: item.id,
                      name: item.name,
                      displayName: item.name,
                      type: item.contentTypeName,
                      typeId: item.contentTypeId,
                      contentDurationInSeconds: item.durationInSeconds,
                      tag: startCase(item.contentTypeName),
                      isCustomContent: false,
                    }));
                    modifiedData.push({
                      id: "custom-content",
                      name: `Use "${
                        media.contentDisplayName ?? media.contentName
                      }" as Expected Content Name`,
                      displayName: media.contentDisplayName ?? media.contentName,
                      type: "custom",
                      typeId: null,
                      contentDurationInSeconds: null,
                      tag: "Custom",
                      isCustomContent: true,
                    });
                    return modifiedData;
                  },
                  onEmpty: () => {
                    onInputChange("", "contentDisplayName");
                    onInputChange("", "contentName");
                    if (media.contentId) {
                      onInputChange("", "contentTypeId");
                      onInputChange("", "contentDurationInSeconds");
                      onInputChange(null, "contentId");
                    }
                  },
                  onSelect: (data) => {
                    onInputChange(true, "isContentSelected"); // Mark that user has selected from dropdown
                    if (data.isCustomContent) {
                      // Handle custom content selection (expected content name)
                      onInputChange(data.displayName, "contentName");
                      onInputChange(data.displayName, "contentDisplayName");
                      onInputChange(null, "contentId"); // Clear contentId for custom content
                      onInputChange("", "contentTypeId"); // Clear contentTypeId, user will need to select manually
                      onInputChange("", "contentDurationInSeconds"); // Clear duration, user will need to enter manually
                    } else {
                      // Handle regular content selection
                      onInputChange(data.displayName || data.name, "contentName");
                      onInputChange(data.displayName || data.name, "contentDisplayName");
                      onInputChange(data.id, "contentId");
                      onInputChange(data.typeId, "contentTypeId");
                      onInputChange(data.contentDurationInSeconds, "contentDurationInSeconds");
                    }
                  },

                  helperText:
                    media.contentName && !media.contentId
                      ? "System will auto-map the Content when CPL with exact name is available"
                      : null,
                  errorMessage: "Please Select Content",
                  error: showError && !this.validateMediaContent("contentId"),
                  disabled: media.contentId && isMediaEditDisabled && !canEditContent,
                },
                {
                  id: "label",
                  type: FORM_FIELD_TYPE.LABEL,
                  value: "(or)",
                  size: { xl: 1, lg: 1, md: 1, sm: 12, xs: 12 },
                  className: styles.cplLabelText,
                  containerClassName: styles.labelOr,
                },

                {
                  type: FORM_FIELD_TYPE.API_SELECT,
                  id: "contentTypeId",
                  title: "Content Type",
                  placeholder: "Select Content Type",
                  size: {
                    lg: "col-lg-4",
                    md: "col-md-6",
                    // lg: 4,
                    // md: 6,
                  },
                  //isLabel: Boolean(media.contentId) && false,
                  fetchAction: () => searchContentTypes(-1, 0, "name"),
                  parseResults: (response) =>
                    response.data.map((item) => ({
                      id: item.id,
                      value: item.name,
                    })),
                  selected: (list) => {
                    return list.filter((item) => item.id === media.contentTypeId);
                  },
                  singleSelect: true,
                  disabled: !!media.contentId,
                  showLabelInButton: false,
                  errorMessage: "Please Select Content Type",
                  //error: showError && !this.validateMediaContent("contentTypeId"),
                  error: showError && isEmpty(media.contentTypeId),
                  // (isEmpty(media.contentId) || isEmpty(media.contentName)),
                  onSelect: ([data]) => onInputChange(data.id, "contentTypeId"),
                },
                {
                  id: "contentDurationInSeconds",
                  title: "Duration (In Seconds)",
                  type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                  size: {
                    lg: 2,
                    md: 5,
                  },
                  //isLabel: Boolean(media.contentId),
                  placeholder: "Enter Duration",
                  disabled: !!media.contentId,
                  value: media.contentDurationInSeconds ? media.contentDurationInSeconds : "",
                  errorMessage: "Please Enter Duration",
                  //error: showError && !this.validateMediaContent("contentDurationInSeconds"),
                  error: showError && !media.contentDurationInSeconds,
                  onValueChange: (value) => onInputChange(value, "contentDurationInSeconds"),
                },
              ],
              {
                id: "brand",
                type: FORM_FIELD_TYPE.API_SEARCH,
                label: "Brand",
                placeholder: "Search for Brand",
                size: {
                  lg: 5,
                  md: 5,
                },
                fetchAction: async (text) => {
                  const res = await searchBrandsWithAdvertiserId({
                    q: text,
                    advertiserId: this.props.campaign.advertiserId,
                  });
                  return res;
                },
                onSelect: (item) => {
                  // clear product categories in case brand removal or brand change
                  // if (!item || item.id !== media?.brandId) {
                  //   removeCategory(-1);
                  // }
                  //onInputChange(item?.type, "brandType");

                  //product categories are cleared by onInputChange function itself
                  if (item?.id === "__add_new__") {
                    onInputChange(null, "brandId");
                    onInputChange("", "brandName");
                    const defaultAdvertiser = campaign?.client
                      ? campaign.client
                      : campaign?.advertiser
                      ? campaign.advertiser
                      : campaign?.advertiserId
                      ? {
                          id: campaign.advertiserId,
                          name: campaign.advertiserName || campaign.advertiserId,
                        }
                      : null;

                    this.props.actions.addBrand(
                      { tabId: "parent-brands" },
                      {
                        name: (media.brandName ?? media.brand?.name ?? "").trim(),
                        advertisers: defaultAdvertiser ? [defaultAdvertiser] : [],
                      },
                      this.handleBrandCreated
                    );
                    return;
                  }
                  return onInputChange(item, "brandId");
                },
                // selected: (list) => {
                //   return list.filter((item) => item.id === media.brandId) ?? [];
                // },
                // query: media.brand?.name ?? "",
                query: media.brandName ?? media.brand?.name ?? "",
                selectedItem:
                  media.brandId && (media.brandName || media.brand?.name)
                    ? {
                        id: media.brandId,
                        name: media.brandName || media.brand?.name || "",
                        displayName: media.brandName || media.brand?.name || "",
                      }
                    : null,
                showSubText: true,
                //isLabel: Boolean(media.contentId),
                parseResults: (response) => {
                  const data = Array.isArray(response) ? response : response?.data || [];
                  const results = data.map((item) => ({
                    id: item[`${item.type}Id`],
                    name: this.renderBrandMenuName(item),
                    value: item[`${item.type}Name`],
                    type: item.type,
                    tag: startCase(item.type),
                  }));
                  return [
                    ...results,
                    {
                      id: "__add_new__",
                      name: "+ Add New Brand",
                      displayName: "+ Add New Brand",
                      isAddNew: true,
                    },
                  ];
                },
                errorMessage: "Please Add Brand",
                //error: showError && !this.validateMediaContent("brandId"),
                error: showError && isEmpty(media.brandId),
                clearSelectOnReset: true,
                //! condition to disable might change in future
              },
              {
                id: "category",
                type: FORM_FIELD_TYPE.MULTI_SELECT,
                label: "Product Category",
                size: {
                  lg: "col-lg-6 col-lg-offset-1",
                  md: "col-md-6 col-md-offset-1",
                },
                tagRenderer: this.categoryMenuRenderer,
                tagInputProps: {
                  placeholder: "Enter Product Category",
                  tagProps: { intent: Intent.NONE, minimal: true },
                  onRemove: (data, index) => removeCategory(index),
                  disabled: !media.brandId,
                  rightElement:
                    media?.categoryIds?.length > 0 ? (
                      <RemoveIcon onClick={() => removeCategory(-1)} />
                    ) : (
                      <SearchIcon />
                    ),
                },
                onQueryChange: this.onBrandQueryChange,
                onClick: () => this.onBrandQueryChange(""),
                selectedItems: media?.categories ?? [],
                items: availableCategories,
                // incase of product categories are unavailable
                noResults: { message: "No Results Found" },
                onItemSelect: (data) => onInputChange(data, "categoryIds"),
                itemRenderer: this.renderCategoryMenuItem,
                resetOnSelect: true,
                //isLabel: Boolean(media.contentId),
                errorMessage: "Please Enter Product Category",
                //error: showError && !this.validateMediaContent("categoryIds"),
                error: showError && isEmpty(media.categoryIds),
              },
              {
                id: "thirdpartyMediaId",
                label: "Thirdparty Media ID",
                type: FORM_FIELD_TYPE.INPUT,
                size: {
                  lg: 5,
                  md: 5,
                },
                errorMessage:
                  showError && !media.thirdpartyMediaId ? "Please Enter Thirdparty Media ID" : "",
                error: !!(showError && !media.thirdpartyMediaId),
                placeholder: "Enter Thirdparty Media ID",
                value: media?.thirdpartyMediaId,
                onChange: (e) => onInputChange(e.target.value, "thirdpartyMediaId"),
              },
            ]}
          />
          {/* <RightPanel
            isOpen={this.state.showAddBrandPanel}
            onClose={this.handleCloseBrandPanel}
            header="Add a New Brand"
            showFooter={false}
          >
            <BrandCreationContainer 
              onClose={this.handleCloseBrandPanel}
              onSuccess={this.handleBrandCreateSuccess}
              campaign={campaign}
              defaultBrandName={this.state.newBrandName}
            />    
          </RightPanel> */}
        </div>
      </InfoBlock>
    );
  }
}
export default memo(BasicDetails);
